import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Nav } from "./Nav";
import { Footer } from "./Footer";
import { TextAndrea } from "./TextAndrea";
import { Reveal } from "./Reveal";
import { ContactFallback } from "./ContactFallback";
import { SLEEP_PAGES } from "../pages/sleepPages";
import { usePageMeta, useFaqSchema, useBreadcrumbSchema } from "../lib/seo";
import { trackEvent } from "../lib/tracking";
import { GoogleStars } from "./GoogleStars";
import { PullQuote } from "./PullQuote";
import {
  GOOGLE_PROFILE_URL,
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
} from "../lib/googleReviews";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, CALENDLY_URL, contactClick } from "../lib/contact";

// Layout + renderer for the standalone SEO pages (/newborn-sleep-consultant
// etc.). Full site chrome (Nav in away-from-home mode, Footer, concierge),
// editorial long-form body in the site's existing typographic language.
// The primary CTA stays the Free Sleep Assessment (Calendly), with the text
// option beside it — no pricing, ever.
export const SeoPage = ({ slug, page: pageProp }) => {
  const page = pageProp || SLEEP_PAGES[slug];
  const [menuOpen, setMenuOpen] = useState(false);
  usePageMeta({ ...page.meta, path: page.path });
  useFaqSchema(page.faq);

  // Hub-and-spoke breadcrumbs: everything rolls up to the national category
  // page (/pediatric-sleep-consultant). Visible row + BreadcrumbList schema.
  // Articles carry explicit crumbs (Home › Sleep Guides › …); the authority
  // and stage pages default to the hub-and-spoke shape below.
  const crumbs = page.crumbs || [
    { label: "Home", href: "/" },
    ...(slug === "pediatric-sleep-consultant"
      ? [{ label: "Pediatric Sleep Consultant" }]
      : [
          { label: "Pediatric Sleep Consultant", href: "/pediatric-sleep-consultant" },
          { label: page.crumb || page.label },
        ]),
  ];
  useBreadcrumbSchema(crumbs);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const book = (location) => () => trackEvent("book_assessment_click", { location });
  const text = (location) => (e) => {
    trackEvent("text_andrea_click", { location });
    contactClick("text")(e);
  };

  return (
    <div className="bg-ivory text-ink antialiased" data-testid={`seo-page-${slug}`}>
      <Nav home={false} open={menuOpen} setOpen={setMenuOpen} />
      <main>
        {/* Page header — doubles as the concierge bar's "hero" sentinel */}
        <header data-testid="hero-section" className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-32 lg:pt-40 pb-14 lg:pb-20">
          <nav aria-label="Breadcrumb" data-testid={`seo-breadcrumbs-${slug}`} className="mb-12">
            <ol className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {crumbs.map((c, i) => (
                <li key={c.label} className="flex items-center gap-3 text-[11px] tracking-[0.16em] uppercase font-semibold">
                  {i > 0 && <span aria-hidden="true" className="text-clay/60">&rsaquo;</span>}
                  {c.href ? (
                    <a href={c.href} className="text-smoke/80 hover:text-ink transition-colors duration-300">
                      {c.label}
                    </a>
                  ) : (
                    <span aria-current="page" className="text-ink/70">{c.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-euca font-semibold">{page.label}</p>
            <h1 data-testid={`seo-h1-${slug}`} className="mt-7 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-ink max-w-4xl">
              {page.h1.map((l) => (
                <span key={l} className="block">{l}</span>
              ))}
            </h1>
            <div className="mt-9 max-w-2xl space-y-5">
              {page.hero.map((p) => (
                <p key={p.slice(0, 24)} className="text-base sm:text-lg font-normal leading-relaxed text-smoke">{p}</p>
              ))}
            </div>
          </Reveal>
          {page.img && (
            <Reveal delay={0.12}>
              <img
                src={page.img}
                alt={page.imgAlt}
                loading="lazy"
                decoding="async"
                className="mt-14 w-full max-w-5xl rounded-[2rem] object-cover aspect-[16/8] shadow-[0_24px_80px_-32px_rgba(36,28,19,0.45)]"
              />
            </Reveal>
          )}
        </header>

        {page.sections.map((s, i) => (
          <section key={s.h2} className={`max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-14 lg:py-20 ${i % 2 === 1 ? "bg-creamsoft" : ""}`}>
            <Reveal>
              <h2 className="font-serif font-light text-3xl sm:text-4xl leading-[1.12] tracking-tight text-ink max-w-3xl">{s.h2}</h2>
              <div className="mt-7 max-w-2xl space-y-5">
                {(s.body || []).map((p) => (
                  <p key={p.slice(0, 24)} className="text-base sm:text-lg font-normal leading-relaxed text-smoke">{p}</p>
                ))}
              </div>
              {s.list && (
                <div className="mt-8 space-y-3.5 max-w-2xl">
                  {s.list.map((item) => (
                    <p key={item.slice(0, 24)} className="flex items-baseline gap-4 text-base sm:text-lg font-normal text-smoke">
                      <span aria-hidden="true" className="h-px w-6 shrink-0 bg-clay translate-y-[-6px]" />
                      {item}
                    </p>
                  ))}
                </div>
              )}
              {s.links && (
                <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                  {s.links.map((l) => (
                    <a key={l.href} href={l.href} className="text-base font-medium text-claydark underline decoration-clay/50 underline-offset-4 hover:text-ink transition-colors duration-300">
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
              {s.andrea && (
                <p className="mt-9 font-serif italic font-light text-2xl sm:text-3xl text-clay leading-snug max-w-2xl">{s.andrea}</p>
              )}
            </Reveal>
          </section>
        ))}

        {page.proof && (
          <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-14 lg:py-20 border-t border-ink/10">
            <Reveal>
              <p className="text-xs tracking-[0.25em] uppercase text-euca font-semibold">
                Andrea Hurley · Certified Pediatric Sleep Consultant
              </p>
              <p
                data-testid={`seo-proof-rating-${slug}`}
                className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2 text-xs tracking-[0.18em] uppercase font-semibold text-ink/70"
              >
                <GoogleStars size={12} />
                {GOOGLE_RATING} on Google · {GOOGLE_REVIEW_COUNT} reviews
                <a
                  href={GOOGLE_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("google_reviews_click", { location: `seo_${slug}` })
                  }
                  className="text-clay underline underline-offset-4 decoration-clay/50 hover:text-ink transition-colors duration-300"
                >
                  Read Reviews on Google
                </a>
              </p>
              <div className="mt-10">
                <PullQuote
                  testid={`seo-proof-${slug}`}
                  quote={page.proof.quote}
                  name={page.proof.name}
                />
              </div>
            </Reveal>
          </section>
        )}

        {page.faq && (
          <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-14 lg:py-20">
            <Reveal>
              <h2 className="font-serif font-light text-3xl sm:text-4xl leading-[1.12] tracking-tight text-ink">A few things parents ask me</h2>
              <div data-testid={`seo-faq-${slug}`} className="mt-9 max-w-2xl space-y-8">
                {page.faq.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-serif font-light text-xl sm:text-2xl leading-snug text-ink">{f.q}</h3>
                    <div className="mt-2.5 space-y-4">
                      {f.a.split("\n\n").map((para) => (
                        <p key={para.slice(0, 24)} className="text-base sm:text-lg font-normal leading-relaxed text-smoke">{para}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </section>
        )}

        {/* Invitation — the assessment as the continuation of the conversation */}
        <section className="bg-claysoft">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-20 lg:py-28">
            <Reveal>
              <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-ink max-w-3xl">
                Tell me what happened last night.
              </h2>
              <p className="mt-6 max-w-xl text-base sm:text-lg font-normal leading-relaxed text-smoke">
                You don&rsquo;t need to have any of this figured out before we talk.
                I&rsquo;ll ask some questions, we&rsquo;ll look at what you&rsquo;ve already
                tried, and I&rsquo;ll tell you what I notice. Then you decide.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-4">
                <a
                  data-testid={`seo-book-cta-${slug}`}
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={book(`seo_${slug}`)}
                  className="inline-flex items-center gap-2.5 rounded-full bg-ink text-ivory text-sm sm:text-base font-medium px-8 py-4 hover:bg-espresso transition-colors duration-300"
                >
                  Book my complimentary pediatric sleep assessment
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a
                  data-testid={`seo-text-cta-${slug}`}
                  href={`sms:${ANDREA_PHONE}`}
                  onClick={text(`seo_${slug}`)}
                  className="text-sm sm:text-base font-medium text-claydark underline decoration-clay/50 underline-offset-4 hover:text-ink transition-colors duration-300"
                >
                  Not ready to book? Text Andrea — {ANDREA_PHONE_DISPLAY}
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Keep reading — internal links between the five pages */}
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-14 lg:py-16">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-euca font-semibold">Keep reading</p>
            <div data-testid={`seo-related-${slug}`} className="mt-6 flex flex-wrap gap-x-8 gap-y-3.5">
              {slug !== "pediatric-sleep-consultant" && (
                <a href="/pediatric-sleep-consultant" className="text-base sm:text-lg font-serif font-light text-ink underline decoration-clay/50 underline-offset-4 hover:text-claydark transition-colors duration-300">
                  Pediatric sleep consulting — the whole picture
                </a>
              )}
              {slug.startsWith("denver") && (
                <a href="/colorado-pediatric-sleep-consultant" className="text-base sm:text-lg font-serif font-light text-ink underline decoration-clay/50 underline-offset-4 hover:text-claydark transition-colors duration-300">
                  Colorado pediatric sleep consulting
                </a>
              )}
              {page.related.map((l) => (
                <a key={l.href} href={l.href} className="text-base sm:text-lg font-serif font-light text-ink underline decoration-clay/50 underline-offset-4 hover:text-claydark transition-colors duration-300">
                  {l.label}
                </a>
              ))}
              <a href="/" className="text-base sm:text-lg font-serif font-light text-smoke underline decoration-ink/20 underline-offset-4 hover:text-ink transition-colors duration-300">
                Back to the beginning
              </a>
            </div>
          </Reveal>
        </section>
      </main>
      <TextAndrea typing={false} flowOpen={false} onStart={() => window.location.assign("/#talk-with-andrea")} />
      <Footer home={false} />
      <ContactFallback />
    </div>
  );
};
